// Permission utility functions

import type { Permission } from '../types/auth';

/**
 * Permission hierarchy levels
 * Higher number = more permissions
 */
const PERMISSION_LEVELS: Record<Permission, number> = {
  read: 1,
  order_placement: 2,
  management: 3,
};

/**
 * Check if a user permission includes the required permission
 * Uses hierarchical permission model:
 * - management includes order_placement and read
 * - order_placement includes read
 * - read is the base level
 *
 * @param userPermission - User's current permission level
 * @param required - Required permission level
 * @returns true if user has required permission or higher
 */
export function hasPermission(userPermission: Permission, required: Permission): boolean {
  return PERMISSION_LEVELS[userPermission] >= PERMISSION_LEVELS[required];
}

/**
 * Get permission level as number (for comparison)
 *
 * @param permission - Permission string
 * @returns Permission level (1-3)
 */
export function getPermissionLevel(permission: Permission): number {
  return PERMISSION_LEVELS[permission];
}

/**
 * Parse permission string to Permission type
 * Throws error if invalid permission
 *
 * @param permission - Permission string from API
 * @returns Typed Permission
 */
export function parsePermission(permission: string): Permission {
  const normalized = permission.toLowerCase();
  if (normalized === 'read' || normalized === 'order_placement' || normalized === 'management') {
    return normalized as Permission;
  }
  throw new Error(`Invalid permission: ${permission}`);
}

/**
 * Format permission for display
 *
 * @param permission - Permission type
 * @returns Human-readable permission name
 */
export function formatPermission(permission: Permission): string {
  switch (permission) {
    case 'read':
      return 'Read';
    case 'order_placement':
      return 'Order Placement';
    case 'management':
      return 'Management';
    default:
      return permission;
  }
}

/**
 * Get all permissions that are at or below the given permission
 *
 * @param permission - User's permission level
 * @returns Array of permissions the user can grant
 */
export function getGrantablePermissions(permission: Permission): Permission[] {
  const level = PERMISSION_LEVELS[permission];
  return (Object.keys(PERMISSION_LEVELS) as Permission[]).filter(
    p => PERMISSION_LEVELS[p] <= level
  );
}
